/**
 * `abrir_cliente` — abre a ficha de UM cliente, na seção pedida ([T015] / US-4).
 *
 * Espelho do `ToolAbrirCliente` do backend. Duas decisões que valem explicar:
 *
 * ★ **Quem entra no contexto do cliente depende da ROTA** ([spec-011 T016]).
 *
 *   `/cliente-perfil/:id?` leva o cliente na URL, e `client-profile.vue`
 *   (`hydrateFromRoute`) hidrata a partir dele: procura na lista, carrega a
 *   lista se preciso, tenta o inativo e só então despacha `enterClient`. Ali,
 *   despachar aqui criaria a segunda régua de "qual é o cliente ativo".
 *
 *   As outras três seções abrem telas do cliente ATIVO — `fp.home`,
 *   `carteira.minha` e `crm.relationship` não declaram param, e o `params.id`
 *   que o guard monta é descartado no push. Com o cliente A no contexto,
 *   `abrir_cliente(B, 'planejamento')` abria o planejamento de A: a IA dizia
 *   "abri o planejamento do Bruno" e o consultor via a Ana. Por isso o
 *   `executar` entra no contexto ANTES de empurrar — a tela monta lendo o
 *   cliente ativo, e inverter a ordem a faria montar com o anterior.
 *
 * ★ **A régua é uma FLAG POR ROTA, não a heurística "tem param".** A
 *   coincidência param ↔ auto-hidratação vale hoje nas quatro seções, e PARA de
 *   valer no deep link `carteira.minha.cliente` (`c/:id_cliente_route`), que
 *   declara o param e NÃO entra no contexto — `minha-carteira.vue:1028-1036` só
 *   faz `addSearchClient` (achado D10/UC-7 do PLAN da spec-009). Com a flag, o
 *   catálogo da 009 pode declarar o fato de cada rota sem que esta regra tenha
 *   de adivinhar; com a heurística, apontar uma seção para o deep link deixaria
 *   o contexto no cliente anterior, em silêncio.
 *
 * ★ **Lista vazia não é recusa.** O guard resolve o cliente contra
 *   `user/allClients` para poder recusar cedo o que está fora da carteira. Mas
 *   quando a lista ainda não chegou (login recente, primeira tela), recusar
 *   transformaria "ainda não carregou" em "você não tem esse cliente" — e o
 *   modelo repetiria a mentira ao usuário. Nesse caso, passa: quem avisa se o
 *   cliente não existe é a própria tela de perfil.
 */
import { filterClients } from '@modules/navegacao-v2/components/client-switcher-helpers'
import {
  temContextoDeNavegacao,
  podeAcessar,
  empurrarRota,
  rotaDoModuloNoCliente,
  MOTIVO_SEM_CONTEXTO,
  ABRIR_TELA_EM_NOVA_ABA,
} from './catalogo'

// [T002b] Teto de candidatos por busca de nome — mesmo VALOR do
// `LIMITE_CANDIDATOS` do backend (`core/visoes/limiares.py`), cópia
// intencional (mecanismo independente, esta tool roda no browser).
const LIMITE_CANDIDATOS_POR_NOME = 10

export const NOME_DA_TOOL = 'abrir_cliente'
export const LABEL = 'Abrir cliente'

/** A rota do PERFIL (nav 2026) — o default quando a seção é omitida. */
export const ROTA_DO_PERFIL = 'cliente-perfil'

/** O deep link da carteira (`c/:id_cliente_route`) — ver a flag abaixo. */
export const ROTA_DA_CARTEIRA_DO_CLIENTE = 'carteira.minha.cliente'

/**
 * As 4 seções do enum do backend (`SECOES_DO_CLIENTE`) → chave do módulo no
 * catálogo da visão CLIENTE (`CLIENTE_GROUPS`).
 *
 * O nome da rota é DERIVADO do catálogo (`rotaDoModuloNoCliente`), nunca escrito
 * à mão: é o catálogo que sabe que a carteira de um cliente é `carteira.minha`
 * (e não a agregada) e que o CRM de um cliente é `crm.relationship`. Uma cópia
 * literal aqui envelheceria na primeira mudança de rota, e o sintoma seria um
 * push para uma rota que não existe mais — silencioso.
 */
/** O param genérico do cliente — o default de toda rota que não declara outro. */
const PARAM_DO_CLIENTE = 'id'

export const MODULO_POR_SECAO = {
  perfil: 'perfil',
  carteira: 'consolidador',
  planejamento: 'fp',
  crm: 'cli-crm',
}

/**
 * `secao → {name, paramKey}` — o destino e o NOME DO PARAM que a rota declara.
 *
 * ★ [spec-009 T018] O param deixou de ser sempre `id`: o deep link da carteira
 * (`carteira.minha.cliente`) declara `:id_cliente_route`. Um `{id}` numa rota
 * que pede outro param é descartado pelo vue-router SEM erro, e a tela abre
 * para o cliente que já estava no contexto — foi assim que
 * `abrir_cliente(B, 'carteira')` abria a carteira do cliente A.
 */
export const ROTA_POR_SECAO = Object.keys(MODULO_POR_SECAO).reduce(
  (mapa, secao) => {
    mapa[secao] = rotaDoModuloNoCliente(MODULO_POR_SECAO[secao]) || {
      name: ROTA_DO_PERFIL,
      paramKey: PARAM_DO_CLIENTE,
    }
    return mapa
  },
  {}
)

/**
 * Campos onde o código do cliente pode estar, do mais específico ao real.
 *
 * ★ `codigo_corretora` é o que a lista de VERDADE traz. `user/allClients` é o
 * spread de `/v2/api/app/listar_clientes` (`api/listaClientes.js`), e ali o
 * código do cliente vem nesse campo — é ele que a plataforma imprime como
 * "Código:" (`components/carteira-nav-bar.vue:439`), que a tabela de clientes
 * lista (`router/views/controle-clientes.vue:128`) e por onde a busca global
 * casa (`components/client-search.vue:48`). Sem ele nesta lista,
 * `codigoDoCliente()` devolvia `''` para TODO cliente e nenhuma busca por
 * código achava ninguém: o pedido mais natural da US-4 ("abre o cliente
 * 17765563") voltava sempre como "o cliente não está na carteira deste
 * profissional" — e o modelo repetia essa falsidade ao usuário. Medido no E2E
 * do [T023] em 26/08/2026, três tentativas, três recusas.
 *
 * Os três primeiros ficam por precedência: se um dia a lista passar a carregar
 * o código canônico do cadastro, ele ganha de `codigo_corretora`.
 */
const CAMPOS_DE_CODIGO = ['codigo_cliente', 'codigo', 'cod_cliente', 'codigo_corretora']

/**
 * As rotas que HIDRATAM o cliente sozinhas a partir do param da URL.
 *
 * Declaração por rota, e não heurística: rota ausente daqui ⇒ quem entra no
 * contexto é este handler.
 *
 * ★ `carteira.minha.cliente` entrou em 27/08/2026, com o fecho do [G-1] do QA
 * da spec-009. Antes ela recebia o cliente pela URL e **não** o abria: o
 * `openRouteClient` da view procurava o cliente com `v.id === id_cliente` — id
 * numérico contra o param, que é sempre string —, então o `find` nunca achava
 * ninguém e a tela ficava no cliente anterior. Agora a view hidrata pelo param
 * no molde do perfil (`minha-carteira.vue`, `hidratarClienteDaRota`), e é por
 * isso que a flag virou: quem hidrata sozinho não precisa que este handler
 * despache `enterClient` antes do push.
 */
export const ROTAS_QUE_HIDRATAM_PELO_PARAM = {
  [ROTA_DO_PERFIL]: true,
  [ROTA_DA_CARTEIRA_DO_CLIENTE]: true,
}

/** A rota se vira sozinha com o `:id` da URL? */
export function rotaHidrataPeloParam(name) {
  return Boolean(ROTAS_QUE_HIDRATAM_PELO_PARAM[name])
}

/**
 * A rota declara o param do cliente? Perguntado ao PRÓPRIO router.
 *
 * ★ Fail-OPEN (`true`) quando o router não expõe as rotas, pela mesma razão de
 * `rotaExisteNoRouter`: sem a lista, quem manda é a flag declarada acima. O
 * router aqui serve para DESMENTIR a flag — uma rota marcada como
 * auto-hidratante que perdeu o param no caminho volta a entrar no contexto por
 * este handler, em vez de abrir a tela do cliente anterior calada.
 */
function rotaDeclaraOParam(ctx, name, paramKey) {
  const router = ctx && ctx.router
  const lista =
    router && typeof router.getRoutes === 'function' ? router.getRoutes() : null
  if (!lista) return true
  const registro = lista.find((rota) => rota && rota.name === name)
  if (!registro || typeof registro.path !== 'string') return true
  return registro.path.indexOf(':' + (paramKey || PARAM_DO_CLIENTE)) !== -1
}

/**
 * Este push precisa de `enterClient` antes?
 *
 * Só quando há um cliente RESOLVIDO: sem a lista carregada não existe objeto
 * para despachar, e um `{id}` cru gravaria um cliente sem nome na barra
 * (`enterClient` repassa o objeto direto para `user/addSearchClient`).
 */
function precisaEntrarNoContexto(ctx, name, paramKey, cliente) {
  if (!cliente) return false
  return !(rotaHidrataPeloParam(name) && rotaDeclaraOParam(ctx, name, paramKey))
}

const recusar = (motivo) => ({ ok: false, motivo: motivo })

/** O id da conversa CORRENTE — lido do getter, nunca de uma segunda régua. */
function conversationIdAtual(ctx) {
  const getters = (ctx && ctx.store && ctx.store.getters) || {}
  const conversa = getters['aawz-intelligence-v2/selectedConversation']
  return (conversa && conversa.id) || null
}

// "000124" e 124 são o mesmo cliente: o usuário digita os zeros, o banco não os
// guarda. Comparar sem normalizar faria o código do usuário nunca casar.
const normalizarCodigo = (valor) => {
  const texto = String(valor === null || valor === undefined ? '' : valor).trim()
  const digitos = texto.replace(/\D/g, '')
  if (!digitos) return texto.toLowerCase()
  return String(Number(digitos))
}

const codigoDoCliente = (cliente) => {
  for (const campo of CAMPOS_DE_CODIGO) {
    if (cliente && cliente[campo] !== undefined && cliente[campo] !== null) {
      return normalizarCodigo(cliente[campo])
    }
  }
  return ''
}

const listaDeClientes = (ctx) => {
  const getters = (ctx && ctx.store && ctx.store.getters) || {}
  const lista = getters['user/allClients']
  return Array.isArray(lista) ? lista : []
}

const preenchido = (valor) => valor !== undefined && valor !== null && valor !== ''

// A busca por NOME (T002b, `filterClients` — mesma régua do palette Ctrl+K),
// isolada: único ou ambíguo, `candidatos` vem preenchido; sem casar ninguém,
// `null` (para `acharCliente` cair no retorno vazio).
function acharPorNome(lista, nome) {
  if (!preenchido(nome)) return null
  const casados = filterClients(lista, nome)
  if (casados.length === 1) return { cliente: casados[0], candidatos: null }
  if (casados.length > 1) return { cliente: null, candidatos: casados }
  return null
}

/**
 * Acha o cliente — por id, depois `codigo`, e só então `nome` (T002b). O id
 * NÃO faz curto-circuito ([T023]: o modelo às vezes põe o código nos dois
 * campos) — `codigo`/`id_cliente_app`-como-código continuam tentados mesmo
 * sem casar por id. `candidatos` só vem preenchido quando `nome` casa mais
 * de um cliente (a tool nunca escolhe sozinha, mesma régua do backend).
 */
function acharCliente(input, ctx) {
  const lista = listaDeClientes(ctx)
  if (!lista.length) return { cliente: null, listaVazia: true, candidatos: null }

  const acharPorCodigo = (valor) => {
    if (!preenchido(valor)) return null
    const codigo = normalizarCodigo(valor)
    if (!codigo) return null
    return lista.find((c) => codigoDoCliente(c) === codigo) || null
  }

  const id = input.id_cliente_app
  if (preenchido(id)) {
    const porId = lista.find((c) => c && String(c.id) === String(id))
    if (porId) return { cliente: porId, listaVazia: false, candidatos: null }
  }

  const porCodigo = acharPorCodigo(input.codigo) || acharPorCodigo(id)
  if (porCodigo) return { cliente: porCodigo, listaVazia: false, candidatos: null }

  const porNome = acharPorNome(lista, input.nome)
  if (porNome) return { ...porNome, listaVazia: false }

  return { cliente: null, listaVazia: false, candidatos: null }
}

/**
 * @param {Object} input `{id_cliente_app?, codigo?, nome?, secao?}`.
 * @returns {{ok: boolean, motivo?: string, rota?: Object, idClienteApp?: number}}
 */
/** Um dos três identificadores do cliente chegou? */
function conferirIdentificacao(dados) {
  const temId = dados.id_cliente_app !== undefined && dados.id_cliente_app !== null
  if (temId || dados.codigo || dados.nome) return null
  return recusar(
    'informe `id_cliente_app` (preferível, quando você o tem de outra tool), ' +
      '`codigo` do cliente OU `nome` (ao menos parte dele) — sem um dos três ' +
      'não há cliente para abrir.'
  )
}

/** A seção existe e o escritório tem acesso a ela? */
function conferirSecao(secao, ctx) {
  const destino = ROTA_POR_SECAO[secao]
  if (!destino) {
    return recusar(
      'a seção "' + secao + '" não existe. Seções válidas: ' +
        Object.keys(ROTA_POR_SECAO).sort().join(', ') + '.'
    )
  }
  if (!podeAcessar(ctx, destino.name)) {
    return recusar(
      'o escritório não tem acesso à seção "' + secao + '" da ficha do cliente.'
    )
  }
  return { ok: true, destino: destino }
}

/**
 * O id com que a rota vai ser aberta — ou a recusa, com o valor que o modelo
 * pediu no motivo (motivo sem o valor faz ele tentar de novo com o mesmo).
 *
 * ★ [T002b] Terceiro desfecho: `nome` casou MAIS DE UM cliente. Não é recusa
 * (o cliente existe — vários, na verdade) nem sucesso (a tool nunca escolhe
 * sozinha, mesma régua do `resolver_cliente(nome=)` do backend) — por isso
 * `requerEscolha: true`, um formato próprio que `guard`/`executar` repassam
 * como `{requer_escolha: true, candidatos, instrucao}` para o MODELO
 * perguntar ao usuário.
 */
function resolverId(dados, ctx) {
  const achado = acharCliente(dados, ctx)

  if (achado.candidatos && achado.candidatos.length) {
    const candidatos = achado.candidatos
      .slice(0, LIMITE_CANDIDATOS_POR_NOME)
      .map((cliente) => ({
        id_cliente_app: cliente.id,
        nome: cliente.name || null,
        codigo: codigoDoCliente(cliente) || null,
      }))
    return {
      ok: false,
      requerEscolha: true,
      candidatos: candidatos,
      instrucao:
        'Há ' + achado.candidatos.length + ' clientes com esse nome. Pergunte ' +
        'ao usuário qual é, citando nome e código, e chame de novo com ' +
        '`id_cliente_app`.',
    }
  }

  if (!achado.cliente && !achado.listaVazia) {
    const pedido = dados.id_cliente_app || dados.codigo || dados.nome
    // [T002b] Buscou SÓ por nome (nem id, nem código no input) e não casou
    // ninguém — a recusa cita os TRÊS formatos aceitos, mesma frase do
    // `FORMATOS_DE_CANDIDATO_DE_CLIENTE` do backend (`core/cliente_
    // declarado.py`), porque aqui o modelo ainda não tentou os outros dois.
    const buscouSoPorNome =
      !preenchido(dados.id_cliente_app) &&
      !preenchido(dados.codigo) &&
      preenchido(dados.nome)
    if (buscouSoPorNome) {
      return recusar(
        'nenhum cliente da carteira deste profissional casa com o nome "' +
          pedido + '". Tente de novo com um id de app_clientes, um codigo ' +
          'de corretora do escritorio ou outra parte do nome do cliente.'
      )
    }
    return recusar(
      'o cliente "' + pedido + '" não está na carteira deste profissional (ou ' +
        'não existe). Confirme o cliente antes de abrir a ficha.'
    )
  }

  const id = achado.cliente ? achado.cliente.id : dados.id_cliente_app
  if (id === undefined || id === null || id === '') {
    return recusar(
      'não consegui resolver o cliente "' + dados.codigo + '" para um id; peça ' +
        'o `id_cliente_app` a uma tool de leitura antes de abrir a ficha.'
    )
  }
  // O OBJETO vai junto: é ele que o `enterClient` repassa ao
  // `user/addSearchClient`, e buscá-lo de novo no `executar` seria a segunda
  // varredura da mesma lista — com chance de divergir desta na primeira mudança.
  return { ok: true, id: id, cliente: achado.cliente }
}

export function guard(input, ctx) {
  const dados = input || {}
  if (!temContextoDeNavegacao(ctx)) return recusar(MOTIVO_SEM_CONTEXTO)

  const semIdentificacao = conferirIdentificacao(dados)
  if (semIdentificacao) return semIdentificacao

  const secao = conferirSecao(dados.secao || 'perfil', ctx)
  if (!secao.ok) return secao
  const destino = secao.destino

  const idOuRecusa = resolverId(dados, ctx)
  // [T002b] Ambiguidade por nome — nem sucesso, nem recusa: sobe intacto
  // para `executar` decidir o formato do output (`requer_escolha`).
  if (idOuRecusa.requerEscolha) return idOuRecusa
  if (!idOuRecusa.ok) return idOuRecusa
  const id = idOuRecusa.id

  // ★ [spec-009 T018] O param sai com o nome que a ROTA declara. Fixar `id`
  // aqui era o defeito da US-7: `carteira.minha` não declara param nenhum, o
  // vue-router descartava o `{id}` calado, e a carteira abria para quem já
  // estava no contexto.
  const params = {}
  params[destino.paramKey] = String(id)

  return {
    ok: true,
    rota: { name: destino.name, params: params },
    paramKey: destino.paramKey,
    idClienteApp: Number(id),
    cliente: idOuRecusa.cliente,
  }
}

/**
 * spec-016 T035 (QA it.3) — grava o cliente que `abrir_cliente` acabou de
 * resolver no estado DA CONVERSA (`aawz-intelligence-v2/setClienteDaConversa`).
 *
 * ★ Incondicional — nova aba ou não. No `/ia` imersivo (`ABRIR_TELA_EM_NOVA_ABA`
 * sempre `true`), a rota abre na ABA NOVA e `navigation/enterClient` (linha
 * abaixo) NUNCA acontece nesta aba — sem este dispatch, a aba de ORIGEM
 * (onde a conversa continua) nunca saberia qual cliente a IA resolveu, e o
 * composer não tinha de onde ler `idClienteAnexo` para o upload de anexo
 * (medido no QA it.3: "abre o cliente Tatiana" → anexa a carteira → "sobe
 * essa carteira" na MESMA conversa → zero requisições de upload).
 */
function despacharClienteDaConversa(ctx, veredito) {
  if (!ctx || !ctx.store || typeof ctx.store.dispatch !== 'function') return
  const cliente = veredito.cliente
  ctx.store.dispatch('aawz-intelligence-v2/setClienteDaConversa', {
    id_cliente_app: veredito.idClienteApp,
    codigo: cliente ? codigoDoCliente(cliente) || null : null,
    nome: cliente ? cliente.nome_cliente || cliente.nome || null : null,
    conversation_id: conversationIdAtual(ctx),
  })
}

/**
 * @returns {Promise<{aberto: boolean, rota: ?Object, id_cliente_app: ?number}>}
 */
export function executar(input, ctx) {
  const veredito = guard(input, ctx)
  // [T002b] Nome ambíguo — nunca uma recusa (o cliente existe; vários,
  // aliás): o modelo recebe os candidatos e a instrução de perguntar ao
  // usuário, sem `recusado`, para não soar como erro de uso da tool.
  if (veredito.requerEscolha) {
    return Promise.resolve({
      aberto: false,
      rota: null,
      id_cliente_app: null,
      requer_escolha: true,
      candidatos: veredito.candidatos,
      instrucao: veredito.instrucao,
    })
  }
  if (!veredito.ok) {
    return Promise.resolve({
      aberto: false,
      rota: null,
      id_cliente_app: null,
      motivo: veredito.motivo,
      recusado: true,
    })
  }

  despacharClienteDaConversa(ctx, veredito)

  // ANTES do push, e não depois: a tela monta lendo o cliente ativo. Invertido,
  // ela montaria com o cliente ANTERIOR e trocaria em seguida — o consultor
  // veria o dado errado antes do certo.
  //
  // ⚠️ Só quando o push acontece NA MESMA aba (`!ABRIR_TELA_EM_NOVA_ABA`). Com
  // `ABRIR_TELA_EM_NOVA_ABA` (hoje sempre true), quem vai ler o cliente ativo é
  // a ABA NOVA — despachar aqui muda o Vuex desta aba (a que ficou aberta, ex.
  // a `/ia`) e pinta a faixa de contexto "Cliente" nela por engano, mesmo o
  // usuário nunca tendo saído dali. A aba nova entra em contexto sozinha, pela
  // própria rota de destino.
  if (
    !ABRIR_TELA_EM_NOVA_ABA &&
    precisaEntrarNoContexto(
      ctx,
      veredito.rota.name,
      veredito.paramKey,
      veredito.cliente
    )
  ) {
    ctx.store.dispatch('navigation/enterClient', veredito.cliente)
  }

  return empurrarRota(ctx, veredito.rota).then((resultadoAbertura) => {
    // ★ O navegador BLOQUEOU o pop-up (feedback do owner, 04/09) — mesmo
    // motivo do `navegar`. `enterClient`, se ia acontecer, JÁ aconteceu acima
    // (síncrono, antes do `empurrarRota`) — não há o que desfazer aqui; o
    // ramo `!ABRIR_TELA_EM_NOVA_ABA` é que decide SE ele despacha, e essa
    // decisão não muda por a aba ter sido bloqueada.
    if (resultadoAbertura && resultadoAbertura.bloqueado) {
      return {
        aberto: false,
        bloqueado: true,
        href: resultadoAbertura.href,
        rota: veredito.rota,
        id_cliente_app: veredito.idClienteApp,
        motivo: resultadoAbertura.motivo,
        recusado: true,
      }
    }
    return {
      aberto: true,
      rota: veredito.rota,
      id_cliente_app: veredito.idClienteApp,
    }
  })
}

export default { nome: NOME_DA_TOOL, label: LABEL, guard: guard, executar: executar }
